import { useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ApiExercise, Platform } from '@codey2/codey-api-types/models';
import { useSnackbar } from '../../navigation/snackbar';
import { useExercisesApi } from '../../app/api/useExercisesApi';
import { useNavigate } from 'react-router-dom';

const validationSchema = Yup.object({
  name: Yup.string().required('Naam is verplicht'),
  description: Yup.string().required('Beschrijving is verplicht'),
  platform: Yup.mixed<Platform>().required('Platform is verplicht'),
  steps: Yup.array().min(1, 'Er moet minimaal één stap zijn').required('Stappen zijn verplicht'),
});

export type FormContent = {
  name: string;
  description: string;
  platform: Platform;
  steps: Step[];
};

export type Step = {
  id: string;
  label: string;
  content: string;
};

interface UseExerciseFormOptions {
  exercise?: ApiExercise;
}

export const useExerciseForm = ({ exercise }: UseExerciseFormOptions = {}) => {
  const { isLoading, createExercise, updateExercise } = useExercisesApi();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const formik = useFormik<FormContent>({
    initialValues: {
      name: '',
      description: '',
      platform: 'SCRATCH',
      steps: [{ id: crypto.randomUUID(), label: 'Stap 1', content: '' }],
    },
    validationSchema,
    onSubmit: async values => {
      if (!isLoading && formik.isValid) {
        try {
          const requestData = {
            title: values.name,
            platform: values.platform,
            introduction: values.description,
            steps: values.steps.map(step => ({
              name: step.label,
              content: step.content,
            })),
          };

          let result: ApiExercise;
          if (exercise) {
            // Update existing exercise
            result = await updateExercise(exercise.id, requestData);
            showSnackbar('Opdracht succesvol bijgewerkt', 'success');
          } else {
            // Create new exercise
            result = await createExercise(requestData);
            showSnackbar('Opdracht succesvol opgeslagen', 'success');
          }

          if (result?.id) navigate(`/opdrachten/${result.id}`);
        } catch {
          const errorMessage = exercise
            ? 'Het bijwerken van de opdracht is mislukt. Probeer het later opnieuw.'
            : 'Het opslaan van de opdracht is mislukt. Probeer het later opnieuw.';
          showSnackbar(errorMessage, 'error');
        }
      }
    },
  });

  // Load exercise data when exercise prop changes
  useEffect(() => {
    if (exercise) {
      formik.setValues({
        name: exercise.title,
        description: exercise.introduction,
        platform: exercise.platform as Platform,
        steps: exercise.steps.map(step => ({
          id: crypto.randomUUID(),
          label: step.name,
          content: step.content,
        })),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exercise]);

  return formik;
};
